import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { safeSessionStorage } from '../../utils/safeStorage';

const DISMISSED_KEY = 'ml-pwa-dismissed';

// Matches the actual iOS Safari share button icon
const IOSShareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}
    strokeLinecap="round" strokeLinejoin="round"
    className="w-4 h-4 inline-block align-[-2px]">
    <polyline points="8 6 12 2 16 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
    <path d="M20 16v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4" />
  </svg>
);

export default function InstallPWA() {
  const { isInstallable, isIOS, promptInstall } = usePWAInstall();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(
    () => safeSessionStorage.getItem(DISMISSED_KEY) === '1'
  );

  // Never inside an embed (the floating widget iframe, EmbedModal iframes) —
  // a visitor on someone else's site should never be told to add *our* site
  // to their home screen.
  if (window.self !== window.top) return null;

  // Only show on mobile, and never on purchase or public creator pages
  if (window.innerWidth >= 1024) return null;
  if (pathname.startsWith('/m/') || pathname.startsWith('/c/')) return null;
  if (dismissed) return null;
  if (!isInstallable && !isIOS) return null;

  const dismiss = () => {
    safeSessionStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
  };

  // ── Android / Chrome — one-tap native install ──────────────────────────────
  if (isInstallable) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50">
        <div className="flex items-center justify-between gap-3 rounded-2xl bg-emerald-600 px-4 py-3 text-white shadow-2xl">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo.png" alt="" className="w-10 h-10 rounded-xl flex-shrink-0 shadow" />
            <div className="min-w-0">
              <p className="text-sm font-bold leading-tight">Install MintLink</p>
              <p className="text-xs text-emerald-100 truncate">Faster experience, works offline</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={async () => { await promptInstall(); dismiss(); }}
              className="rounded-xl bg-white px-3 py-1.5 text-xs font-bold text-emerald-700 shadow hover:bg-emerald-50 transition"
            >
              Install
            </button>
            <button onClick={dismiss} className="p-1 text-emerald-200 hover:text-white transition">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── iOS Safari — guided Add to Home Screen sheet ───────────────────────────
  return (
    <>
      <style>{`
        @keyframes ml-pwa-up {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .ml-pwa-sheet { animation: ml-pwa-up 0.35s cubic-bezier(0.32,0.72,0,1) forwards; }
      `}</style>

      {/* Backdrop */}
      <div className="fixed inset-0 z-[60] bg-black/40" onClick={dismiss} />

      {/* Sheet */}
      <div className="ml-pwa-sheet fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl px-5 pt-4 pb-10 shadow-2xl">
        {/* Drag handle */}
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />

        {/* Close */}
        <button onClick={dismiss}
          className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition">
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <img src="/logo.png" alt="" className="w-12 h-12 rounded-2xl shadow-sm" />
          <div>
            <p className="font-extrabold text-gray-900 text-base leading-tight">Install MintLink</p>
            <p className="text-xs text-gray-400 mt-0.5">Add to your Home Screen — it's free</p>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-blue-600">1</span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Tap the{' '}
              <span className="inline-flex items-center gap-1 font-semibold text-blue-600">
                <IOSShareIcon /> Share
              </span>{' '}
              button at the bottom of Safari
            </p>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-blue-600">2</span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Scroll down and tap{' '}
              <span className="font-semibold text-gray-900">"Add to Home Screen"</span>
            </p>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-blue-600">3</span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Tap <span className="font-semibold text-gray-900">"Add"</span> in the top-right corner
            </p>
          </div>
        </div>

        <button onClick={dismiss}
          className="w-full py-3 rounded-2xl text-sm font-semibold text-gray-500 border border-gray-200 hover:bg-gray-50 transition">
          Maybe later
        </button>
      </div>
    </>
  );
}
